import type { ReactNode } from 'react';
import {
  CalendarBlankIcon,
  CheckIcon,
  GenderFemaleIcon,
  GenderMaleIcon,
  IdentificationCardIcon,
  InfoIcon,
  PencilSimpleIcon,
  PhoneIcon,
  SparkleIcon,
} from '@phosphor-icons/react';
import { DocMatchNote } from '../fields/traveler_notes.js';
import './checkout_traveler_card.css';

/** O que o cartão recolhido mostra do viajante. Só entra o que o produto coleta. */
export interface CheckoutTravelerChips {
  /** Código do país (ISO alpha-2 ou alpha-3): a bandeira vem dele quando conhecida. */
  nationality?: string;
  gender?: 'female' | 'male';
  /** Data já formatada. */
  birthdate?: string;
  /** Documento já mascarado. */
  document?: string;
  /** O telefone é exigido e falta: o chip leva ao campo. */
  phonePending?: boolean;
}

export interface CheckoutTravelerCardCopy {
  you: string;
  edit: string;
  done: string;
  genderFemale: string;
  genderMale: string;
  phonePending: string;
  /** Procedência do preenchimento, no rodapé do cartão. */
  inheritedHint: string;
  /** A mesma procedência no cartão de outra pessoa: o perfil não é «seu» (#3841). */
  inheritedHintOther: string;
}

/** O rateio do viajante: o campo (com o farol, quando há), uma nota de que o rateio é
 *  do solicitante, ou o aviso de que o viajante não tem nada a alocar. */
export type CheckoutTravelerAllocation =
  | { kind: 'field'; field: ReactNode; lighthouse?: ReactNode }
  | { kind: 'from-requester'; hint?: string }
  | { kind: 'unavailable'; message: string };

export interface CheckoutTravelerCardProps {
  /** O nome; enquanto o form do viajante carrega, o do roster. */
  name?: string;
  /** Iniciais do avatar; ausente ⇒ sem avatar. */
  initials?: string;
  isRequester: boolean;
  chips: CheckoutTravelerChips;
  /** Há campos de identidade a editar (o pedido à equipe não coleta identidade). */
  editable: boolean;
  editing: boolean;
  onToggleEditing: (editing: boolean) => void;
  /** Campos de identidade de linha inteira que abrem a edição (o nome de embarque). */
  leadFields: ReactNode[];
  /** Campos de identidade curtos, dois por fileira, em edição. */
  pairedFields: ReactNode[];
  /** Os demais campos de identidade, um por linha, depois dos curtos, em edição. */
  fields: ReactNode[];
  /** Ausente enquanto o form do viajante carrega. */
  allocation?: CheckoutTravelerAllocation;
  /** Nota de que o nome deve bater com o documento. */
  docMatchNote?: string;
  copy: CheckoutTravelerCardCopy;
}

/** O cartão do viajante do checkout (Figma 405-5518): identidade, rateio e
 *  procedência numa moldura só. Recolhido, a identidade vira chips; o lápis abre os
 *  campos. */
export function CheckoutTravelerCard({
  name,
  initials,
  isRequester,
  chips,
  editable,
  editing,
  onToggleEditing,
  leadFields,
  pairedFields,
  fields,
  allocation,
  docMatchNote,
  copy,
}: Readonly<CheckoutTravelerCardProps>) {
  return (
    <div className="pcm-traveler ctc-card">
      <div className="ctc">
        <div className="ctc-header">
          {initials && (
            <span className="ctc-avatar" aria-hidden="true">
              {initials}
            </span>
          )}
          <div className="ctc-id">
            <div className="ctc-name-row">
              <h3 className="ctc-name">{name ?? ''}</h3>
              {isRequester && <span className="ctc-you">{copy.you}</span>}
            </div>
            <TravelerChipsRow
              chips={chips}
              copy={copy}
              onOpenEditor={() => {
                onToggleEditing(true);
              }}
            />
          </div>
          {/* Lápis Editar / ✓ Concluir na ponta direita da linha do header (node
              405:5576). Sem campo de identidade não há o que editar. */}
          {editable && (
            <button
              type="button"
              className="ctc-action"
              aria-pressed={editing}
              aria-label={editing ? copy.done : copy.edit}
              onClick={() => {
                onToggleEditing(!editing);
              }}
            >
              {editing ? (
                <CheckIcon size={16} aria-hidden />
              ) : (
                <PencilSimpleIcon size={16} aria-hidden />
              )}
            </button>
          )}
        </div>
        {editing && leadFields}
        {editing && pairedFields.length > 0 && (
          <div className="ctc-traveler-row">{pairedFields}</div>
        )}
        {editing && fields}
      </div>
      {allocation && <TravelerAllocation allocation={allocation} />}
      {docMatchNote && <DocMatchNote text={docMatchNote} />}
      <p className="pcm-inherited">
        <SparkleIcon size={14} weight="fill" aria-hidden />
        <span>{isRequester ? copy.inheritedHint : copy.inheritedHintOther}</span>
      </p>
    </div>
  );
}

function TravelerAllocation({ allocation }: { readonly allocation: CheckoutTravelerAllocation }) {
  switch (allocation.kind) {
    case 'field':
      return (
        <div className="pcm-alloc">
          {allocation.field}
          {allocation.lighthouse}
        </div>
      );
    case 'from-requester':
      // O rateio é do solicitante: sem alocação a pedir, e catálogo vazio aqui não é erro.
      return allocation.hint ? <p className="pcm-alloc-note">{allocation.hint}</p> : null;
    case 'unavailable':
      // Sinalizado por viajante (o envio já fica travado) em vez de derrubar o checkout.
      return (
        <p className="pcm-no-corp" role="alert">
          <InfoIcon size={14} aria-hidden />
          <span>{allocation.message}</span>
        </p>
      );
    default: {
      const exhaustive: never = allocation;
      return exhaustive;
    }
  }
}

const ALPHA2_BY_ALPHA3: Record<string, string> = {
  ARG: 'AR',
  BRA: 'BR',
  CHL: 'CL',
  COL: 'CO',
  ESP: 'ES',
  MEX: 'MX',
  PER: 'PE',
  PRT: 'PT',
  USA: 'US',
};

function flagFromCountryCode(code: string): string | undefined {
  const alpha2 = code.length === 2 ? code : ALPHA2_BY_ALPHA3[code.toUpperCase()];
  if (!alpha2) return undefined;
  return alpha2
    .toUpperCase()
    .split('')
    .map((letter) => {
      const codePoint = letter.codePointAt(0);
      return codePoint === undefined ? '' : String.fromCodePoint(127397 + codePoint);
    })
    .join('');
}

function TravelerChip({ icon, value }: { readonly icon?: ReactNode; readonly value?: ReactNode }) {
  if (!value) return null;
  return (
    <span className="ctc-chip">
      {icon && <span className="ctc-chip-icon">{icon}</span>}
      <span>{value}</span>
    </span>
  );
}

function NationalityChip({ code }: { readonly code: string }) {
  const flag = flagFromCountryCode(code);
  return (
    <TravelerChip
      value={
        <>
          {flag && (
            <span className="ctc-chip-flag" aria-hidden="true">
              {flag}
            </span>
          )}
          {code}
        </>
      }
    />
  );
}

function TravelerChipsRow({
  chips,
  copy,
  onOpenEditor,
}: {
  readonly chips: CheckoutTravelerChips;
  readonly copy: CheckoutTravelerCardCopy;
  readonly onOpenEditor: () => void;
}) {
  const female = chips.gender === 'female';
  return (
    <div className="ctc-chips">
      {chips.nationality && <NationalityChip code={chips.nationality} />}
      {chips.gender && (
        <TravelerChip
          icon={
            female ? (
              <GenderFemaleIcon size={15} aria-hidden />
            ) : (
              <GenderMaleIcon size={15} aria-hidden />
            )
          }
          value={female ? copy.genderFemale : copy.genderMale}
        />
      )}
      <TravelerChip icon={<CalendarBlankIcon size={15} aria-hidden />} value={chips.birthdate} />
      <TravelerChip
        icon={<IdentificationCardIcon size={15} aria-hidden />}
        value={chips.document}
      />
      {/* O telefone exigido nunca entra nos obrigatórios do perfil: sem ele o CTA
          trava, então o cartão recolhido denuncia a pendência e leva ao campo (#4206). */}
      {chips.phonePending && (
        <button
          type="button"
          className="ctc-chip cursor-pointer border-0 bg-warning-soft font-medium text-warning-strong"
          onClick={onOpenEditor}
        >
          <span className="ctc-chip-icon">
            <PhoneIcon size={15} aria-hidden />
          </span>
          <span>{copy.phonePending}</span>
        </button>
      )}
    </div>
  );
}
